import { useState } from 'react';
import { ChevronDown, CheckCircle2, ExternalLink, Plug } from 'lucide-react';
import { toast } from 'sonner';

const categories = [
  {
    name: 'AI Model Providers',
    integrations: [
      { name: 'OpenAI / Azure OpenAI', connected: true, tier: 'Tier 1', records: '14,832 invocations captured', sync: 'Real-time', enriches: 'DER model_id, version, token_count, input hash', proves: 'The model invoked was the approved version. Token count and model hash match the DER. Input and output were captured at invocation time.' },
      { name: 'Anthropic Claude / AWS Bedrock', connected: true, tier: 'Tier 1', records: '8,203 invocations captured', sync: 'Real-time', enriches: 'DER via CloudTrail → S3 → Apex', proves: 'AWS CloudTrail confirms this Bedrock invocation occurred at this timestamp from this principal. Independent timestamp corroboration.' },
      { name: 'Google Vertex AI', connected: false, proves: 'Vertex prediction logs would corroborate model endpoint and prediction metadata.' },
      { name: 'Hugging Face / Self-Hosted', connected: false, proves: 'Self-hosted model inference logs would provide model hash verification.' },
    ],
  },
  {
    name: 'SIEM and Security Platforms',
    integrations: [
      { name: 'Microsoft Sentinel', connected: true, tier: 'Tier 1 + Tier 2', records: '847 security events ingested', sync: '2 min ago', enriches: 'DER security context · Bidirectional', proves: 'No active security incident was correlated with this AI decision at decision time. The decision did not occur during a known breach or alert window.' },
      { name: 'Splunk SIEM', connected: true, tier: 'Tier 2', records: 'User behavior risk scores', sync: '15 min ago', enriches: 'DER security context', proves: 'User behavior risk score at review time was within normal parameters. No anomalous access pattern correlated with this decision.' },
      { name: 'CrowdStrike Falcon', connected: false, proves: 'Endpoint security status was healthy at invocation time. No endpoint compromise correlated with the reviewing principal.' },
      { name: 'Palo Alto Cortex XSIAM', connected: false, proves: 'No unusual data movement was associated with this AI invocation at network level.' },
    ],
  },
  {
    name: 'Identity and Access Management',
    integrations: [
      { name: 'Microsoft Entra ID', connected: true, tier: 'Tier 2', records: '47 reviewer identities verified', sync: 'Real-time', enriches: 'All OERs', proves: 'The reviewer was who they claimed to be. They held the appropriate role at review time. MFA was active. Their session was legitimate.' },
      { name: 'Okta', connected: false, proves: 'Identity provider session confirms reviewer authentication and group membership at review.' },
      { name: 'Active Directory (On-Premise)', connected: false, proves: 'Legacy enterprise identity confirmation for reviewer role and authorization.' },
    ],
  },
  {
    name: 'GRC Platforms',
    integrations: [
      { name: 'ServiceNow GRC', connected: true, tier: 'Tier 2', records: 'Policy v2.3 confirmed', sync: '15 min ago', enriches: 'All CSSs · Bidirectional', proves: 'The policy version active at decision time was formally approved through change management and matches the organization\'s GRC record. Control attestation status is current.' },
      { name: 'OneTrust', connected: false, proves: 'Data source consent and privacy obligation status at the time the data was used.' },
      { name: 'Vanta / Drata / Secureframe', connected: false, proves: 'Current SOC 2 control test results mapped to AI governance evidence requirements.' },
    ],
  },
  {
    name: 'Data Catalogs and Lineage',
    integrations: [
      { name: 'Collibra', connected: true, tier: 'Tier 2', records: '12 data assets registered', sync: '1 hour ago', enriches: 'OERs + DER source fields', proves: 'Every data source used in this decision had current license and consent basis at decision time. Data owner and classification confirmed.' },
      { name: 'Alation', connected: false, proves: 'Data stewardship assignment and policy tags for datasets at time of use.' },
      { name: 'dbt (data build tool)', connected: false, proves: 'Transformation lineage — how raw data was processed before the model used it.' },
    ],
  },
  {
    name: 'MLOps and Model Platforms',
    integrations: [
      { name: 'MLflow', connected: true, tier: 'Tier 3', records: '14 model versions tracked', sync: '30 min ago', enriches: 'Change Control records', proves: 'This model version was produced by this training run, approved through this evaluation, and deployed via this registered pipeline.' },
      { name: 'AWS SageMaker', connected: true, tier: 'Tier 2', records: 'Model Monitor drift alerts', sync: '1 hour ago', enriches: 'Change Control + Integrity Monitoring', proves: 'Model registry approval and endpoint deployment events confirm authorized deployment. Drift alerts provide independent integrity signal.' },
      { name: 'Azure Machine Learning', connected: false, proves: 'AzureML model registration and responsible AI dashboard metrics at deployment time.' },
    ],
  },
  {
    name: 'HR and Learning Management',
    integrations: [
      { name: 'Workday', connected: false, tier: 'Tier 3', proves: 'EU AI Act Art. 4 evidence — the reviewer completed required AI literacy training, holds a current certification, and is authorized to review this workflow type.' },
      { name: 'Cornerstone / SAP SuccessFactors', connected: false, proves: 'Training completion and certification records for authorized reviewers.' },
    ],
  },
  {
    name: 'Workflow and Ticketing',
    integrations: [
      { name: 'Jira', connected: true, tier: 'Tier 2', records: 'Bidirectional', sync: '5 min ago', enriches: 'Incident records', proves: 'The incident remediation followed a tracked, auditable process. Resolution evidence links to the Jira ticket trail.' },
      { name: 'ServiceNow ITSM', connected: true, tier: 'Tier 2', records: 'Bidirectional', sync: '10 min ago', enriches: 'Change Control records', proves: 'Model change approvals went through the organization\'s standard change management process before taking effect.' },
      { name: 'PagerDuty', connected: false, proves: 'On-call response to integrity alerts was acknowledged within SLA at this timestamp.' },
    ],
  },
];

const activeIntegrations = [
  { name: 'Microsoft Sentinel', tier: 'Tier 1', sync: '2 min ago', stat: '847 events enriching DERs', enriches: 'CSS security context' },
  { name: 'ServiceNow GRC', tier: 'Tier 2', sync: '15 min ago', stat: 'Policy v2.3 confirmed', enriches: 'All CSSs' },
  { name: 'Microsoft Entra ID', tier: 'Tier 2', sync: 'Real-time', stat: '47 reviewer identities verified', enriches: 'All OERs' },
  { name: 'Collibra', tier: 'Tier 2', sync: '1 hour ago', stat: '12 data assets verified', enriches: 'OERs + DER sources' },
];

export default function IntegrationsHub() {
  const [openCat, setOpenCat] = useState(categories[0].name);
  const connected = categories.flatMap(c => c.integrations).filter(i => i.connected).length;
  const total = categories.flatMap(c => c.integrations).length;

  return (
    <div data-testid="integrations-hub" className="space-y-6">
      {/* Header */}
      <div>
        <div className="text-sm text-[#94A3B8] mb-1">{total} connectors available · {connected} connected · Enriching 43,218 DERs</div>
        <div className="text-xs text-[#64748B]">Apex Trust does not replace your existing tools. It turns their signals into governance proof.</div>
      </div>

      {/* Proof grade note */}
      <div className="px-4 py-2.5 rounded-lg bg-[#080D1A] border border-[#1E2A45] border-l-[3px] border-l-[#6366F1]">
        <span className="text-[10px] text-[#64748B]">External integrations enrich and corroborate governance proof. Proof grade reflects capture method and field completeness — connections strengthen confidence, they do not automatically upgrade evidentiary quality.</span>
      </div>

      {/* Active Integration Health Strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {activeIntegrations.map((intg) => (
          <div key={intg.name} className="glass-card p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#F8FAFC]">{intg.name}</span>
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">◆ Active · {intg.tier}</span>
            </div>
            <div className="space-y-1 text-[10px]">
              <div className="text-[#64748B]">Last sync: {intg.sync}</div>
              <div className="text-[#94A3B8]">{intg.stat}</div>
              <div className="text-[#475569]">Enriching: {intg.enriches}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Integration Categories */}
      <div className="space-y-2">
        {categories.map((cat) => (
          <div key={cat.name} className="glass-card overflow-hidden">
            <button
              onClick={() => setOpenCat(openCat === cat.name ? null : cat.name)}
              className="w-full flex items-center justify-between p-4 hover:bg-[#0F1629]/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Plug size={16} className="text-[#6366F1]" />
                <span className="text-sm font-semibold text-[#F8FAFC]">{cat.name}</span>
                <span className="text-[10px] text-[#64748B]">{cat.integrations.filter(i => i.connected).length}/{cat.integrations.length} connected</span>
              </div>
              <ChevronDown size={14} className={`text-[#475569] transition-transform ${openCat === cat.name ? 'rotate-180' : ''}`} />
            </button>
            {openCat === cat.name && (
              <div className="px-4 pb-4 space-y-3">
                {cat.integrations.map((intg) => (
                  <div key={intg.name} className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45]" data-testid={`integration-${intg.name.toLowerCase().replace(/[\s\/]+/g, '-')}`}>
                    {/* Left — connection info */}
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-sm font-medium text-[#F8FAFC]">{intg.name}</span>
                      </div>
                      {intg.connected ? (
                        <div className="space-y-1 text-[10px]">
                          <span className="font-mono px-1.5 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">◆ Connected · {intg.tier}</span>
                          <div className="text-[#94A3B8] mt-1">{intg.records}</div>
                          <div className="text-[#475569]">Last sync: {intg.sync}</div>
                          <div className="text-[#475569]">Enriching: {intg.enriches}</div>
                          <button onClick={() => toast.info(`Opening ${intg.name} configuration`)} className="text-[9px] text-[#6366F1] hover:text-[#818cf8] mt-1">Configure →</button>
                        </div>
                      ) : (
                        <div className="space-y-1 text-[10px]">
                          <span className="font-mono px-1.5 py-0.5 rounded-full bg-[#475569]/10 text-[#475569] border border-[#475569]/20">○ Available · Not Connected</span>
                          <button onClick={() => toast.info(`Connecting to ${intg.name}...`)} className="text-[10px] font-medium text-[#6366F1] hover:text-[#818cf8] bg-[#6366F1]/10 px-3 py-1.5 rounded-lg mt-2 block transition-colors">Connect →</button>
                        </div>
                      )}
                    </div>
                    {/* Right — what this proves */}
                    <div className="border-l border-[#1E2A45] pl-4">
                      <div className="text-[9px] font-semibold text-[#475569] uppercase tracking-wider mb-1.5">What this proves</div>
                      <div className="text-[10px] text-[#94A3B8] leading-relaxed italic">"{intg.proves}"</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
